import { useEffect, useState, type FormEvent, type ReactNode } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { Plus, RefreshCw, RotateCcw, Sparkles } from 'lucide-react'
import { Button } from '../../core/index.js'
import type { ScoringAdminApi } from '../adminApi.js'
import type { BehaviorsAdminApi } from '../../behaviors/index.js'
import type { ClientsAdminApi } from '../../clients/index.js'
import type { FacilitiesAdminApi } from '../../facilities/index.js'
import type { BehaviorDirection } from '../../behaviors/index.js'
import { BehaviorChannel } from './BehaviorChannel.js'
import { Carousel } from './Carousel.js'
import { Fader } from './Fader.js'
import { ScoreReadout } from './ScoreReadout.js'
import { SimulatedWorkerBanner } from './SimulatedWorkerBanner.js'
import type { BehaviorsState } from '../../behaviors/index.js'
import {
  DEFAULT_SETTINGS,
  STANDARD_KEYS,
  STANDARD_LABELS,
  defaultCustomSetting,
  invalidChannels,
  normalize,
  perfectMonth,
  takeHandoff,
  withClientBehaviors,
  withFacilityConfig,
  type BehaviorsSource,
  type CustomBasis,
  type CustomChannel,
  type SimulatedWorker,
  type SimulatorSettings,
  type StandardKey,
} from './simulatorModel.js'

/** The admin calls this screen uses. */
type AdminApi = ScoringAdminApi & BehaviorsAdminApi & ClientsAdminApi & FacilitiesAdminApi

// Long enough that dragging a fader sends a handful of requests rather than one per pixel; short enough to feel live.
const RECALCULATE_AFTER_MS = 120
const MAX_SHIFTS = 40
const MAX_CUSTOM_CHANNELS = 20

const STANDARD_CAPTIONS: Record<StandardKey, string> = {
  call_out_rate: 'call-outs ÷ scheduled',
  on_time_arrival: 'on time ÷ worked',
  charting_completion: 'charting reported',
  shift_completion: 'completed ÷ worked',
  no_show_rate: 'no-shows ÷ scheduled',
}

function useSettled<T>(value: T): T {
  const [settled, setSettled] = useState(value)
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), RECALCULATE_AFTER_MS)
    return () => clearTimeout(timer)
  }, [value])
  return settled
}

/** `paged` lays the contents out as a carousel rather than one row that scrolls sideways. */
function Bank({ title, hint, children, paged }: { title: string; hint: string; children: ReactNode; paged?: string }) {
  return (
    // The panels stay dark, like equipment on a desk, so the lit readouts and lamps keep their contrast on the light page.
    <section className="rounded-lg border border-zinc-700 bg-zinc-900 p-4 shadow-sm">
      <div className="mb-3 flex flex-wrap items-baseline gap-x-3">
        <h2 className="text-[11px] font-black uppercase tracking-[3px] text-coral">{title}</h2>
        <p className="text-[11px] text-white/50">{hint}</p>
      </div>
      {paged ? <Carousel label={paged}>{children}</Carousel> : <div className="flex gap-3 overflow-x-auto pb-2">{children}</div>}
    </section>
  )
}

/** A fader with a lamp over it that switches the value between "none" and a number - for inputs that can be absent. */
function OptionalFader({ label, value, fallback, onChange, format }: { label: string; value: number | null; fallback: number; onChange: (value: number | null) => void; format: (value: number) => string }) {
  const isOn = value !== null
  return (
    <div className="flex shrink-0 flex-col items-center gap-1.5">
      <Fader label={label} value={value ?? fallback} min={0} max={100} disabled={!isOn} format={(shown) => (isOn ? format(shown) : 'none')} onChange={onChange} />
      <button
        type="button"
        role="switch"
        aria-checked={isOn}
        aria-label={`${label} has a value`}
        onClick={() => onChange(isOn ? null : fallback)}
        className={`rounded-sm border px-1.5 py-0.5 text-[9px] font-black tracking-widest ${isOn ? 'border-green bg-green/20 text-green' : 'border-white/25 text-white/40'}`}
      >
        {isOn ? 'SET' : 'NONE'}
      </button>
    </div>
  )
}

const CUSTOM_CAPTIONS: Record<CustomBasis, string> = {
  per_period: 'custom · count per period',
  per_shift: 'custom · count ÷ scheduled',
  per_period_minimum: 'custom · minimum per period',
}

function AddChannelForm({ existingKeys, onAdd }: { existingKeys: readonly string[]; onAdd: (channel: CustomChannel) => void }) {
  const [label, setLabel] = useState('')
  const [basis, setBasis] = useState<CustomBasis>('per_period')
  const [direction, setDirection] = useState<BehaviorDirection>('positive')
  const key = `custom_${label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '')}`.slice(0, 40)
  const canAdd = label.trim() !== '' && key.length >= 8 && !existingKeys.includes(key)
  const selectClass = 'rounded-sm border border-white/25 bg-black/40 px-2 py-1.5 text-xs text-white focus:border-coral focus:outline-none'

  function handleSubmit(event: FormEvent): void {
    event.preventDefault()
    if (canAdd) {
      onAdd({ key, label: label.trim(), basis, occurrences: 0, setting: defaultCustomSetting(basis, direction) })
      setLabel('')
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex w-56 shrink-0 flex-col gap-2 rounded border border-dashed border-white/25 p-3">
      <div className="text-xs font-black uppercase tracking-wide text-white/70">Add a custom channel</div>
      <input aria-label="Custom behavior name" value={label} maxLength={60} onChange={(event) => setLabel(event.target.value)} placeholder="e.g. In-service completed" className={selectClass} />
      <select aria-label="What is counted" value={basis} onChange={(event) => setBasis(event.target.value as CustomBasis)} className={selectClass}>
        <option value="per_period">Counted per period</option>
        <option value="per_shift">Counted per scheduled shift</option>
        <option value="per_period_minimum" disabled={direction === 'negative'}>
          Counted against a minimum per period
        </option>
      </select>
      <select
        aria-label="Which way it counts"
        value={direction}
        onChange={(event) => {
          const chosen = event.target.value as BehaviorDirection
          setDirection(chosen)
          // A minimum is "at least this many", which only a behavior that counts for the worker can have.
          if (chosen === 'negative' && basis === 'per_period_minimum') {
            setBasis('per_period')
          }
        }}
        className={selectClass}
      >
        <option value="positive">Counts for the worker</option>
        <option value="negative">Counts against the worker</option>
      </select>
      <Button icon={Plus} size="sm" type="submit" disabled={!canAdd}>
        Add channel
      </Button>
    </form>
  )
}

/**
 * The scoring engine as a mixing desk, for the platform admin to learn and
 * tune it: every number the calculation uses is a fader, and the score is
 * recalculated by the API as they move. It scores made-up activity; no
 * client's data is read or changed.
 */
export function ScoreSimulatorPage({ api }: { api: AdminApi }) {
  const [settings, setSettings] = useState<SimulatorSettings>(DEFAULT_SETTINGS)
  const [clientId, setClientId] = useState('')
  const [clientState, setClientState] = useState<BehaviorsState | null>(null)
  const [source, setSource] = useState<BehaviorsSource>({ kind: 'master' })
  const [loadError, setLoadError] = useState<string | null>(null)
  const [loaded, setLoaded] = useState<string | null>(null)
  const [subject, setSubject] = useState<SimulatedWorker | null>(null)
  // A real worker's score, opened from the admin worker detail: its exact engine input becomes the desk's settings.
  useEffect(() => {
    const handoff = takeHandoff()
    if (handoff) {
      setSettings(normalize(handoff.settings))
      setSubject(handoff.subject ?? null)
      if (handoff.subject) {
        // A worker's score: the pickers go to their client and facility, and that facility's behaviors are loaded over the handed-over ones.
        const { clientId: workerClientId, facilityId } = handoff.subject
        void load(workerClientId, facilityId === null ? { kind: 'master' } : { kind: 'facility', facilityId }, `Opened ${handoff.label}. `)
      } else {
        setLoaded(`Opened ${handoff.label}. Every fader is at the value that score was computed from; move any of them to see what would change.`)
      }
    }
  }, [])
  const settled = useSettled(settings)
  const clashes = invalidChannels(settled)

  const update = (change: (current: SimulatorSettings) => SimulatorSettings): void => setSettings((current) => normalize(change(current)))
  const setActivity = (changes: Partial<SimulatorSettings['activity']>): void => update((current) => ({ ...current, activity: { ...current.activity, ...changes } }))
  const setRules = (changes: Partial<SimulatorSettings['rules']>): void => update((current) => ({ ...current, rules: { ...current.rules, ...changes } }))
  const setThresholds = (changes: Partial<SimulatorSettings['tierThresholds']>): void => update((current) => ({ ...current, tierThresholds: { ...current.tierThresholds, ...changes } }))
  const setCustom = (key: string, changes: Partial<CustomChannel>): void =>
    update((current) => ({ ...current, customBehaviors: current.customBehaviors.map((channel) => (channel.key === key ? { ...channel, ...changes } : channel)) }))

  const scoreQuery = useQuery({
    queryKey: ['admin', 'score-simulation', settled],
    queryFn: () => api.simulateScore(settled),
    enabled: clashes.length === 0,
    placeholderData: keepPreviousData,
    staleTime: Infinity,
  })
  const clientsQuery = useQuery({ queryKey: ['admin', 'clients'], queryFn: () => api.listClients() })
  const clients = (clientsQuery.data ?? []).filter((client) => client.clientId !== null)

  /**
   * Brings a client's behaviors onto the desk: which are on, their weights
   * and anchors, and a channel for each custom behavior the client has
   * defined - from the master, or from what one facility actually applies,
   * in which case that facility's scoring rules and tier thresholds come
   * too. Runs as soon as a client or facility is chosen - picking one and
   * seeing nothing change would look like it has nothing of its own.
   * `opened` goes in front of the note about what was loaded.
   */
  async function load(chosenClientId: string, chosenSource: BehaviorsSource, opened = ''): Promise<void> {
    setClientId(chosenClientId)
    setSource(chosenSource)
    setLoadError(null)
    setLoaded(null)
    if (chosenClientId === '') {
      setClientState(null)
      return
    }
    try {
      const state = await api.behaviorsFor(chosenClientId).load()
      setClientState(state)
      const facility = chosenSource.kind === 'facility' ? state.facilities.find((candidate) => candidate.facilityId === chosenSource.facilityId) : undefined
      const config = facility ? await api.facilityConfig(chosenClientId, facility.facilityId).catch(() => null) : null
      update((current) => {
        const withBehaviors = withClientBehaviors(current, state, chosenSource)
        return config ? withFacilityConfig(withBehaviors, config) : withBehaviors
      })
      const customCount = state.behaviors.filter((behavior) => behavior.kind === 'custom').length
      const name = clients.find((client) => client.clientId === chosenClientId)?.organizationName ?? 'the client'
      const what = facility ? `${facility.name}’s effective settings${config ? ', scoring rules and tier thresholds' : ' (it has no configuration yet, so the rules and thresholds are unchanged)'}` : `${name}’s master settings`
      setLoaded(`${opened}Loaded ${what}, with anchors, and ${customCount === 0 ? 'no custom behaviors (it has none)' : `${customCount} custom ${customCount === 1 ? 'behavior' : 'behaviors'}`}.`)
    } catch (err) {
      setLoadError(err instanceof Error && err.message !== '' ? err.message : 'Could not load that client’s behaviors')
    }
  }

  const { activity, rules, tierThresholds } = settings
  const worked = activity.scheduledShifts - activity.callOuts - activity.noShows
  const result = scoreQuery.data
  const resultFor = (key: string) => result?.inputs.find((input) => input.key === key)
  const error = clashes.length > 0 ? `Give ${clashes.join(', ')} different levels for 100 and 0 to calculate.` : scoreQuery.isError ? scoreQuery.error.message || 'The calculation failed' : null

  return (
    <div className="px-6 py-6">
      <div className="mx-auto flex max-w-6xl flex-col gap-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <p className="max-w-2xl text-xs leading-relaxed text-ink-mid">
            Every number the scoring engine uses is a fader. Move one and the score is recalculated.{' '}
            {subject
              ? `The desk starts from ${subject.workerName}’s real activity for the period; nothing you move here is saved.`
              : 'This scores made-up activity for one worker over one time frame; it reads and changes no client’s data.'}{' '}
            Open <span className="font-bold text-ink">API calls</span> to see each request.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              icon={RotateCcw}
              variant="secondary"
              size="sm"
              onClick={() => {
                setSettings(DEFAULT_SETTINGS)
                setClientId('')
                setClientState(null)
                setSource({ kind: 'master' })
                setLoaded(null)
                setLoadError(null)
                setSubject(null)
              }}
            >
              Reset
            </Button>
            <Button icon={Sparkles} variant="secondary" size="sm" onClick={() => update(perfectMonth)}>
              Perfect month
            </Button>
            <select
              aria-label="Client to load behaviors from"
              value={clientId}
              onChange={(event) => void load(event.target.value, { kind: 'master' })}
              className="rounded border border-marine/30 bg-white px-2 py-1.5 text-xs text-ink focus:border-marine focus:outline-none"
            >
              <option value="">Load a client’s behaviors…</option>
              {clients.map((client) => (
                <option key={client.registrationId} value={client.clientId ?? ''}>
                  {client.organizationName}
                </option>
              ))}
            </select>
            {clientState && (
              <select
                aria-label="Master or facility to load"
                value={source.kind === 'facility' ? source.facilityId : ''}
                onChange={(event) => void load(clientId, event.target.value === '' ? { kind: 'master' } : { kind: 'facility', facilityId: event.target.value })}
                className="rounded border border-marine/30 bg-white px-2 py-1.5 text-xs text-ink focus:border-marine focus:outline-none"
              >
                <option value="">Master</option>
                {clientState.facilities.map((facility) => (
                  <option key={facility.facilityId} value={facility.facilityId}>
                    {facility.name}
                  </option>
                ))}
              </select>
            )}
            <Button icon={RefreshCw} variant="secondary" size="sm" disabled={clientId === ''} onClick={() => void load(clientId, source)}>
              Reload
            </Button>
          </div>
        </div>
        {loadError && <p className="text-xs font-bold text-coral">{loadError}</p>}
        {loaded && <p className="text-xs text-ink-mid">{loaded} Custom channels come after the standard ones in the behavior channels.</p>}

        <div className="sticky top-0 z-10 flex flex-col gap-2">
          {subject && <SimulatedWorkerBanner subject={subject} />}
          {/* While anchors clash nothing is calculated, so the last result is for other settings and stays dimmed. */}
          <ScoreReadout result={result} isStale={scoreQuery.isFetching || settled !== settings || clashes.length > 0} error={error} />
        </div>

        <Bank title="Time frame and activity" hint={`What happened: ${activity.scheduledShifts} scheduled, ${worked} worked.`}>
          <Fader label="Day span" value={settings.periodDays} min={1} max={90} format={(value) => `${value}d`} onChange={(periodDays) => update((current) => ({ ...current, periodDays }))} />
          <Fader label="Scheduled" value={activity.scheduledShifts} min={0} max={MAX_SHIFTS} onChange={(scheduledShifts) => setActivity({ scheduledShifts })} note="shifts" />
          <Fader label="Call-outs" value={activity.callOuts} min={0} max={activity.scheduledShifts - activity.noShows} onChange={(callOuts) => setActivity({ callOuts })} />
          <Fader label="Short notice" value={activity.shortNoticeCallOuts} min={0} max={activity.callOuts} onChange={(shortNoticeCallOuts) => setActivity({ shortNoticeCallOuts })} note="of call-outs" />
          <Fader label="No-shows" value={activity.noShows} min={0} max={activity.scheduledShifts - activity.callOuts} onChange={(noShows) => setActivity({ noShows })} />
          <Fader label="On time" value={activity.onTimeArrivals} min={0} max={worked} onChange={(onTimeArrivals) => setActivity({ onTimeArrivals })} note={`of ${worked} worked`} />
          <Fader label="Completed" value={activity.completedShifts} min={0} max={worked} onChange={(completedShifts) => setActivity({ completedShifts })} note={`of ${worked} worked`} />
          <Fader label="Charts due" value={activity.chartsDue} min={0} max={worked} onChange={(chartsDue) => setActivity({ chartsDue })} note={`of ${worked} worked`} />
          <Fader label="Charted" value={activity.chartsCompleted} min={0} max={activity.chartsDue} onChange={(chartsCompleted) => setActivity({ chartsCompleted })} note={activity.chartsDue > 0 ? `of ${activity.chartsDue} due` : 'none due'} />
          <OptionalFader label="Charting %" value={activity.chartingPct} fallback={90} format={(value) => `${value}%`} onChange={(chartingPct) => setActivity({ chartingPct })} />
          <Fader label="Bridge shifts" value={activity.clinicalBridgeShifts} min={0} max={10} onChange={(clinicalBridgeShifts) => setActivity({ clinicalBridgeShifts })} />
          <Fader label="PTO denied, worked" value={activity.ptoDeniedWorkedShifts} min={0} max={worked} onChange={(ptoDeniedWorkedShifts) => setActivity({ ptoDeniedWorkedShifts })} note={`of ${worked} worked`} />
          <Fader label="Late fills" value={activity.lateFills} min={0} max={worked} onChange={(lateFills) => setActivity({ lateFills })} note="inside 24 h, worked" />
          <Fader label="Late Bridge pulls" value={activity.lateBridgeWithdrawals} min={0} max={10} onChange={(lateBridgeWithdrawals) => setActivity({ lateBridgeWithdrawals })} note="inside the cutoff" />
          <OptionalFader label="Previous score" value={rules.previousScore} fallback={75} format={String} onChange={(previousScore) => setRules({ previousScore })} />
        </Bank>

        <Bank title="Behavior channels" paged="behavior channels" hint="For each behavior: how much it counts, the level that earns 100, and the level that earns 0.">
          {STANDARD_KEYS.map((key) => (
            <BehaviorChannel
              key={key}
              label={STANDARD_LABELS[key]}
              caption={STANDARD_CAPTIONS[key]}
              setting={settings.behaviors[key]}
              result={resultFor(key)}
              anchorUnit="rate"
              onChange={(setting) => update((current) => ({ ...current, behaviors: { ...current.behaviors, [key]: setting } }))}
            />
          ))}
          {settings.customBehaviors.map((channel) => (
            <BehaviorChannel
              key={channel.key}
              label={channel.label}
              caption={CUSTOM_CAPTIONS[channel.basis]}
              setting={channel.setting}
              result={resultFor(channel.key)}
              anchorUnit={channel.basis === 'per_shift' ? 'rate' : 'count'}
              minimumOnly={channel.basis === 'per_period_minimum'}
              leading={<Fader label="Count" group={channel.label} value={channel.occurrences} min={0} max={20} onChange={(occurrences) => setCustom(channel.key, { occurrences })} />}
              onChange={(setting) => setCustom(channel.key, { setting })}
              onRemove={() => update((current) => ({ ...current, customBehaviors: current.customBehaviors.filter((other) => other.key !== channel.key) }))}
            />
          ))}
          {settings.customBehaviors.length < MAX_CUSTOM_CHANNELS && (
            <AddChannelForm
              existingKeys={settings.customBehaviors.map((channel) => channel.key)}
              onAdd={(channel) => update((current) => ({ ...current, customBehaviors: [...current.customBehaviors, channel] }))}
            />
          )}
        </Bank>

        <Bank title="Rules and tiers" hint="What happens around the weighted average, and where each tier begins.">
          <Fader label="Min shifts" value={rules.minShifts} min={0} max={20} onChange={(minShifts) => setRules({ minShifts })} note="0 = off" />
          <Fader label="Short notice ×" value={rules.shortNoticeMultiplier} min={1} max={3} step={0.1} format={(value) => `×${value.toFixed(1)}`} onChange={(shortNoticeMultiplier) => setRules({ shortNoticeMultiplier })} note="1 = off" />
          <Fader label="Bridge boost" value={rules.bridgeBoostPerShift} min={0} max={10} step={0.5} format={(value) => `+${value}`} onChange={(bridgeBoostPerShift) => setRules({ bridgeBoostPerShift })} note="per shift" />
          <Fader label="PTO worked" value={rules.ptoDeniedWorkedPoints} min={0} max={10} step={0.5} format={(value) => `+${value}`} onChange={(ptoDeniedWorkedPoints) => setRules({ ptoDeniedWorkedPoints })} note="per day" />
          <Fader label="Late fill" value={rules.lateFillPoints} min={0} max={10} step={0.5} format={(value) => `+${value}`} onChange={(lateFillPoints) => setRules({ lateFillPoints })} note="per shift" />
          <Fader label="Late pull" value={rules.lateBridgeWithdrawalPoints} min={0} max={10} step={0.5} format={(value) => `−${value}`} onChange={(lateBridgeWithdrawalPoints) => setRules({ lateBridgeWithdrawalPoints })} note="per withdrawal" />
          <Fader label="Std period" value={settings.standardPeriodDays} min={1} max={90} format={(value) => `${value}d`} onChange={(standardPeriodDays) => update((current) => ({ ...current, standardPeriodDays }))} note="for per-period" />
          <div aria-hidden="true" className="mx-1 w-px shrink-0 bg-white/15" />
          <Fader label="Gold from" value={tierThresholds.goldMinScore} min={0} max={100} onChange={(goldMinScore) => setThresholds({ goldMinScore })} />
          <Fader label="Silver from" value={tierThresholds.silverMinScore} min={0} max={tierThresholds.goldMinScore} onChange={(silverMinScore) => setThresholds({ silverMinScore })} />
          <Fader label="Bronze from" value={tierThresholds.bronzeMinScore} min={0} max={tierThresholds.silverMinScore} onChange={(bronzeMinScore) => setThresholds({ bronzeMinScore })} />
        </Bank>
      </div>
    </div>
  )
}
